import React, { useEffect, useMemo, useState } from "react";
// Styles
import MorningMeetingContainer from "./MorningMeetings.styles";

// Components
import SearchOptions from "components/Common/SearchOptions/SearchOptions";
import HomeList from "components/Screens/Home/HomeList/HomeList";
import { Pagination } from "@mui/material";

// Utils
import { useSettings } from "context/Settings.context";
import * as Database from "utils/database.utils";
import MorningMeetingRow from "../../../components/Screens/Company/MorningMeetingRow/MorningMeetingRow";
import { MorningMeeting } from "./MorningMeetings.types";
import { faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import Button from "../../../components/Common/Button/Button";
import MorningMeetingModal from "../MorningMeetingModal/MorningMeetingModal";
import { MorningMeetingForm } from "../MorningMeetingModal/MorningMeetingModal.types";
import SelectTimeModal from "../../../components/Screens/BQMQ/Setup/SelectTimeModal/SelectTimeModal";
import Tooltip from "@mui/material/Tooltip";
import { useHistory } from "react-router-dom";

const PAGE_SIZE = 30;

const FILTER_OPTIONS: FilterOption[] = [
    {
        id: 1,
        name: "Search",
        value: "search",
        type: "SEARCH",
    },
    {
        id: 2,
        name: "Date",
        value: "date",
        type: "DROPDOWN",
        searchable: false,
        options: [
            {
                label: "Ascending",
                value: "asc",
            },
            {
                label: "Descending",
                value: "desc",
            },
        ],
    },
];

const MorningMeetings = () => {
    const history = useHistory();

    const { setShowLoadingToast } = useSettings();
    const [filterQuery, setFilterQuery] = useState("");
    const [morningMeetings, setMorningMeetings] = useState<MorningMeeting[]>(
        []
    );
    const [nextMorningMeetingId, setNextMorningMeetingId] = useState<
        string | null
    >(null);
    const [filteredMorningMeetings, setFilteredMorningMeetings] = useState<
        MorningMeeting[]
    >([]);
    const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [upcomingMeetingTimeModalOpen, setUpcomingMeetingTimeModalOpen] =
        useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = useMemo(
        () => Math.ceil(filteredMorningMeetings.length / PAGE_SIZE),
        [filteredMorningMeetings.length]
    );

    const paginatedMeetings = useMemo(
        () =>
            filteredMorningMeetings.slice(
                (currentPage - 1) * PAGE_SIZE,
                currentPage * PAGE_SIZE
            ),
        [filteredMorningMeetings, currentPage]
    );

    const getAllMorningMeetings = async () => {
        const response = await Database.get(
            "api/bqmq_voting/morning-meetings?include_cancelled=true"
        );
        if (response.ok) {
            const data = await response.json();
            setMorningMeetings(data.data);
            setFilteredMorningMeetings(data.data);
            setCurrentPage(1);
        }
    };

    const getMorningMeetingScheduledTime = async () => {
        const response = await Database.get(
            "api/bqmq_voting/morning-meetings-scheduled-time"
        );
        if (response.ok) {
            const data = await response.json();
            // Parse time string (format: "HH:MM:SS" or "HH:MM:SS.microseconds")
            const timeStr = data.data.scheduled_at;
            const [hours, minutes, seconds] = timeStr
                .split(":")
                .map((part: string) => parseInt(part.split(".")[0], 10));
            const time = new Date();
            time.setHours(hours, minutes, seconds || 0, 0);
            setScheduledTime(time);
        }
    };

    const extractFilterFromQuery = (filter: string) => {
        //extract all the filter values from the filter query,
        // if not present default to empty string
        return (
            (filterQuery.includes(filter) &&
                filterQuery.split(`&filter_${filter}=`).pop()?.split("&")[0]) ||
            ""
        );
    };

    const filterAndSortMorningMeetings = (
        searchValue: string,
        dateValue: string
    ) => {
        let filteredData = [...morningMeetings];
        filteredData = filteredData.filter((meeting) =>
            meeting.title.toLowerCase().includes(searchValue.toLowerCase())
        );

        if (dateValue === "asc") {
            filteredData = filteredData.sort(
                (a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime()
            );
        } else if (dateValue === "desc") {
            filteredData = filteredData.sort(
                (a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
            );
        }

        setFilteredMorningMeetings(filteredData);
    };

    const createMorningMeeting = async (formValue: MorningMeetingForm) => {
        setShowLoadingToast({
            show: true,
            message: "CREATING",
        });

        const payload = {
            title: formValue.title,
            date: formValue.date?.toISOString(),
        };

        const response = await Database.post(
            "api/bqmq_voting/morning-meetings",
            payload
        );

        if (response.ok) {
            setModalOpen(false);
            setShowLoadingToast({
                show: false,
                message: "CREATING",
            });
            setLoading(true);
            await getAllMorningMeetings();
            setLoading(false);
        }
    };

    const editUpcomingMeetingsTime = async (time: Date) => {
        setShowLoadingToast({
            show: true,
            message: "UPDATING",
        });

        // Extract time components and send as time string (format: "HH:MM:SS")
        const hours = time.getHours();
        const minutes = time.getMinutes();
        const seconds = time.getSeconds();
        const timeStr = `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

        const payload = {
            scheduled_at: timeStr,
        };

        const response = await Database.post(
            "api/bqmq_voting/morning-meetings-scheduled-time",
            payload
        );

        if (response.ok) {
            setUpcomingMeetingTimeModalOpen(false);
            setScheduledTime(time);
        }

        setShowLoadingToast({
            show: false,
            message: "UPDATING",
        });
    };

    const getButtonTooltipText = (): string => {
        if (!morningMeetings || morningMeetings.length < 1)
            return "No morning meetings found. Create a meeting first.";
        if (!nextMorningMeetingId)
            return "There is no upcoming morning meeting. Create a future meeting first.";

        return "";
    };

    const navigateToAddAgendaItem = (meetingId: string) => {
        history.push(`/portfolio/morning-meetings/${meetingId}/agenda-item`);
    };

    useEffect(() => {
        const search = extractFilterFromQuery("search");
        const date = extractFilterFromQuery("date");

        filterAndSortMorningMeetings(search, date);
        setCurrentPage(1);
    }, [filterQuery]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            await Promise.all([
                getAllMorningMeetings(),
                getMorningMeetingScheduledTime(),
            ]);

            setLoading(false);
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (loading) {
            setShowLoadingToast({
                show: true,
                message: "LOADING",
            });
        } else {
            setShowLoadingToast({
                show: false,
                message: "LOADING",
            });
        }
    }, [loading]);

    // Effect for finding next meeting id
    useEffect(() => {
        if (!morningMeetings || morningMeetings.length === 0) return;

        const londonTz = "Europe/London";
        const now = new Date(
            new Date().toLocaleString("en-US", { timeZone: londonTz })
        );

        // Meetings are already returned from the server in descending order by date
        const meetingDatesMap = morningMeetings.map((m) => ({
            id: m.id,
            date: new Date(
                new Date(m.date).toLocaleString("en-US", {
                    timeZone: londonTz,
                })
            ),
        }));

        let nextMeetingId = undefined;

        for (const { id, date } of meetingDatesMap) {
            if (date.getTime() <= now.getTime()) break;
            if (date.getTime() > now.getTime()) nextMeetingId = id;
        }

        if (nextMeetingId) setNextMorningMeetingId(nextMeetingId);
    }, [morningMeetings]);

    return (
        <MorningMeetingContainer>
            {modalOpen && (
                <MorningMeetingModal
                    onClose={() => setModalOpen(false)}
                    onSubmit={createMorningMeeting}
                ></MorningMeetingModal>
            )}
            {upcomingMeetingTimeModalOpen && (
                <SelectTimeModal
                    initialTime={scheduledTime}
                    onClose={() => setUpcomingMeetingTimeModalOpen(false)}
                    onSubmit={editUpcomingMeetingsTime}
                ></SelectTimeModal>
            )}
            <SearchOptions
                setFilterQuery={setFilterQuery}
                options={FILTER_OPTIONS}
                noDebounce
            />
            <div className="action-buttons-wrapper">
                <Tooltip title={getButtonTooltipText()} placement="top">
                    <div>
                        <Button
                            text="ADD AGENDA ITEM TO NEXT MEETING"
                            icon={faPlus}
                            click={() =>
                                nextMorningMeetingId &&
                                navigateToAddAgendaItem(nextMorningMeetingId)
                            }
                            disabled={
                                loading ||
                                !morningMeetings ||
                                morningMeetings.length < 1 ||
                                !nextMorningMeetingId
                            }
                        />
                    </div>
                </Tooltip>
                <Button
                    text="EDIT UPCOMING MEETINGS TIME"
                    icon={faPencil}
                    click={() => setUpcomingMeetingTimeModalOpen(true)}
                />
                <Button
                    text="NEW MEETING"
                    icon={faPlus}
                    click={() => setModalOpen(true)}
                />
            </div>
            <HomeList
                data={paginatedMeetings}
                row={(row: any) => <MorningMeetingRow data={row} />}
                title={"MORNING MEETINGS"}
                countColor={""}
                loading={loading}
                count={morningMeetings.length}
            />
            {morningMeetings.length > 0 && (
                <div className="pagination-wrapper">
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                    />
                </div>
            )}
        </MorningMeetingContainer>
    );
};
export default MorningMeetings;
